import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileInvoiceDollar, FaEye } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { mapCustomerListItemFromApi, getCustomerSelectLabel } from "../utils/formatters";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import "../assets/css/ViewCustomer.css";

const formatMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Finance → Tenants: outstanding balances + links into Invoices & Payments.
 */
function FinanceTenantsTab({ onOpenInvoices }) {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [tenRes, arRes] = await Promise.all([
          axiosInstance.get("/clients").catch(() => ({ data: [] })),
          axiosInstance.get("/fico/accounts-receivable", { params: { include_items: false } }).catch(() => ({ data: [] }))
        ]);
        const rawTenants = Array.isArray(tenRes.data) ? tenRes.data : tenRes.data?.data || [];
        const mapped = rawTenants
          .map((item) => mapCustomerListItemFromApi(item))
          .filter(Boolean);
        const arRows = Array.isArray(arRes.data) ? arRes.data : arRes.data?.data || [];
        if (!cancelled) {
          setTenants(mapped);
          setInvoices(arRows);
        }
      } catch (err) {
        console.error("Failed to load tenants finance summary:", err);
        if (!cancelled) setError(err.response?.data?.error || "Failed to load tenants.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const byClient = new Map();
    invoices.forEach((inv) => {
      const cid = String(inv.client_id ?? inv.Client_Id ?? "");
      if (!cid) return;
      const prev = byClient.get(cid) || { invoiceCount: 0, unpaidCount: 0, balance: 0, total: 0, paid: 0 };
      const bal = Number(inv.Balance_Amount) || 0;
      const tot = Number(inv.Total_Amount) || 0;
      const paid = Number(inv.Paid_Amount) || 0;
      const status = String(inv.Status || "");
      prev.invoiceCount += 1;
      prev.balance += bal;
      prev.total += tot;
      prev.paid += paid;
      if (bal > 0.001 && status !== "Cancelled" && status !== "Paid") {
        prev.unpaidCount += 1;
      }
      byClient.set(cid, prev);
    });

    const q = search.trim().toLowerCase();
    return tenants
      .map((t) => {
        const stats = byClient.get(String(t.id)) || {
          invoiceCount: 0,
          unpaidCount: 0,
          balance: 0,
          total: 0,
          paid: 0
        };
        return { ...t, ...stats, label: getCustomerSelectLabel(t) };
      })
      .filter((t) => {
        if (!q) return true;
        return String(t.label || "").toLowerCase().includes(q) || String(t.id).includes(q);
      })
      .sort((a, b) => b.balance - a.balance || String(a.label).localeCompare(String(b.label)));
  }, [tenants, invoices, search]);

  return (
    <div className="viewlead-section tenant-invoices-section finance-tenants-panel">
      <div className="viewlead-section-header viewlead-section-header--filters-only">
        <div className="viewlead-section-actions">
          <input
            type="text"
            className="viewlead-search-input"
            placeholder="Search tenants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="tenant-invoices-error">{error}</p>}

      {loading ? (
        <p className="viewlead-no-data">Loading tenants...</p>
      ) : (
        <>
          <div className="tenant-invoices-table-wrap co-desktop-table">
            <table className="viewlead-table tenant-invoices-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Invoices</th>
                  <th>Unpaid</th>
                  <th>Invoiced</th>
                  <th>Paid</th>
                  <th>Outstanding</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="viewlead-no-data">
                      No tenants found.
                    </td>
                  </tr>
                ) : (
                  rows.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <button
                          type="button"
                          className="table-name-link tenant-invoices-link-btn"
                          onClick={() => navigate(`/tenants/${t.id}/view`)}
                        >
                          {t.label || `Tenant #${t.id}`}
                        </button>
                      </td>
                      <td>{t.invoiceCount}</td>
                      <td>{t.unpaidCount}</td>
                      <td>{formatMoney(t.total)}</td>
                      <td>{formatMoney(t.paid)}</td>
                      <td>
                        <strong>{formatMoney(t.balance)}</strong>
                      </td>
                      <td>
                        <div className="tenant-invoices-actions">
                          <button
                            type="button"
                            className="tenant-invoices-action-btn"
                            style={{ width: "auto", padding: "4px 10px", fontSize: 12 }}
                            title="View invoices"
                            onClick={() => onOpenInvoices && onOpenInvoices(t.id)}
                          >
                            Invoices
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <ResultsMobileList emptyLabel="No tenants found." className="finance-tenants-mobile">
            {rows.map((t) => {
              const outstanding = Number(t.balance) || 0;
              const statusTone = outstanding > 0.001 ? "warning" : "success";
              return (
                <ResultsMobileCard
                  key={t.id}
                  title={t.label || `Tenant #${t.id}`}
                  subtitle={outstanding > 0.001 ? `Outstanding ${formatMoney(t.balance)}` : "No outstanding balance"}
                  tone={outstanding > 0.001 ? "warning" : undefined}
                  meta={[
                    {
                      label: outstanding > 0.001 ? `${t.unpaidCount} unpaid` : "Settled",
                      tone: statusTone,
                    },
                  ]}
                  fields={[
                    { label: "Invoices", value: t.invoiceCount },
                    { label: "Invoiced", value: formatMoney(t.total) },
                    { label: "Paid", value: formatMoney(t.paid) },
                    { label: "Outstanding", value: formatMoney(t.balance) },
                  ]}
                  onOpen={() => navigate(`/tenants/${t.id}/view`)}
                  actions={
                    <div className="co-mcard__action-grid">
                      <button
                        type="button"
                        className="co-maction"
                        title="View tenant"
                        aria-label="View tenant"
                        onClick={() => navigate(`/tenants/${t.id}/view`)}
                      >
                        <FaEye size={14} />
                        <span>View</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction"
                        title="View invoices"
                        aria-label="View invoices"
                        onClick={() => onOpenInvoices && onOpenInvoices(t.id)}
                      >
                        <FaFileInvoiceDollar size={14} />
                        <span>Invoices</span>
                      </button>
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
        </>
      )}
    </div>
  );
}

export default FinanceTenantsTab;
